import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FinancialConfig } from './FinancialConfig.js';
import { SimulationEngine } from './SimulationEngine.js';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';

const SMSFQuickRetirementCheck = ({ smsfState, setSmsfState }) => {
    const [activeTab, setActiveTab] = useState('scenarios');
    
    // Quick Check Inputs
    const [inputs, setInputs] = useState({
        currentAge: 65,
        retirementAge: 65,
        currentBalance: 1000000,
        desiredIncome: 60000,
        hasPartner: false,
        partnerAge: 63,
        partnerBalance: 500000,
        homeOwned: true,
        includePension: true,
        projectionYears: 10,
        lumpSumYear: 0,
        lumpSumAmount: 0
    });

    const [results, setResults] = useState(null);
    const [selectedScenario, setSelectedScenario] = useState('realistic');
    const [selectedPensionType, setSelectedPensionType] = useState('account_based');

    // Scenarios with realistic return assumptions
    const scenarios = {
        optimistic: {
            name: 'Optimistic',
            icon: '📈',
            color: '#10b981',
            description: 'Above-average returns (9% p.a.)',
            returnRate: 0.09,
            inflationRate: 0.025
        },
        realistic: {
            name: 'Realistic',
            icon: '📊',
            color: '#3b82f6',
            description: 'Historical average (7% p.a.)',
            returnRate: 0.07,
            inflationRate: 0.025
        },
        pessimistic: {
            name: 'Pessimistic',
            icon: '📉',
            color: '#ef4444',
            description: 'Below-average returns (5% p.a.)',
            returnRate: 0.05,
            inflationRate: 0.030
        }
    };

    // Pension types with characteristics
    const pensionTypes = {
        account_based: {
            name: 'Account-Based Pension',
            icon: '💰',
            description: 'Flexible drawdown with market-based returns',
            features: [
                '✅ Full flexibility to vary income',
                '✅ Potential for capital growth',
                '✅ Full balance to beneficiaries on death',
                '⚠️ Minimum drawdown requirements apply',
                '⚠️ Market risk (balance can go up or down)'
            ],
            minDrawdown: true,
            taxRate: 0,
            flexibility: 'High',
            risk: 'Market',
            estate: 'Full balance'
        },
        transition_to_retirement: {
            name: 'Transition to Retirement (TTR)',
            icon: '🔄',
            description: 'Continue working while drawing pension',
            features: [
                '✅ Keep working part-time',
                '✅ Salary sacrifice more to super',
                '✅ Tax-free pension income (if 60+)',
                '⚠️ Maximum 10% drawdown limit',
                '⚠️ Minimum 4-5% drawdown required',
                '⚠️ Converts to Account-Based at full retirement'
            ],
            minDrawdown: true,
            maxDrawdown: 0.10,
            taxRate: 0,
            flexibility: 'Medium',
            risk: 'Market',
            estate: 'Reverts to accumulation'
        },
        lifetime_annuity: {
            name: 'Lifetime Annuity',
            icon: '🛡️',
            description: 'Guaranteed income for life (not available in SMSF)',
            features: [
                '✅ Guaranteed income regardless of markets',
                '✅ Protection from longevity risk',
                '✅ Income for life (even if balance exhausted)',
                '❌ Not available within SMSF structure',
                '⚠️ Little to no flexibility once purchased',
                '⚠️ Limited or no estate benefit'
            ],
            minDrawdown: false,
            taxRate: 0,
            flexibility: 'Low',
            risk: 'None',
            estate: 'Limited or none',
            notAvailableSMSF: true
        }
    };

    // Calculate minimum drawdown based on age
    const calculateMinDrawdown = (age) => {
        const rates = FinancialConfig.MINIMUM_PENSION_STANDARDS.WITHDRAWAL_RATES;
        if (age < 65) return 0.04;
        if (age < 75) return 0.05;
        if (age < 80) return 0.06;
        if (age < 85) return 0.07;
        if (age < 90) return 0.09;
        if (age < 95) return 0.11;
        return 0.14;
    };

    // Run quick projection
    const runQuickProjection = () => {
        const scenario = scenarios[selectedScenario];
        const pensionType = pensionTypes[selectedPensionType];
        const projectionData = [];
        
        let balance = inputs.currentBalance;
        let partnerBalance = inputs.hasPartner ? inputs.partnerBalance : 0;
        let age = inputs.currentAge;
        let partnerAge = inputs.hasPartner ? inputs.partnerAge : 0;
        
        const totalBalance = balance + partnerBalance;
        
        for (let year = 0; year <= inputs.projectionYears; year++) {
            const minDrawdownRate = calculateMinDrawdown(age);
            const minDrawdown = totalBalance > 0 ? balance * minDrawdownRate : 0;
            
            // TTR has max drawdown of 10%
            let actualIncome = inputs.desiredIncome;
            if (selectedPensionType === 'transition_to_retirement' && balance > 0) {
                const maxTTR = balance * 0.10;
                actualIncome = Math.min(actualIncome, maxTTR);
            }
            actualIncome = Math.max(actualIncome, minDrawdown);
            
            // Lump sum withdrawal
            const lumpSum = (year === inputs.lumpSumYear) ? inputs.lumpSumAmount : 0;
            
            // Returns and fees
            const investmentReturn = balance * scenario.returnRate;
            const fees = balance * 0.015; // 1.5% typical SMSF fees
            
            // Age Pension (simplified calculation)
            let agePension = 0;
            if (inputs.includePension && age >= 67) {
                const assessableAssets = balance + partnerBalance;
                const isCouple = inputs.hasPartner;
                agePension = SimulationEngine.calculatePension(
                    assessableAssets,
                    actualIncome,
                    isCouple,
                    inputs.homeOwned,
                    year,
                    0.003 // pension growth rate
                );
            }
            
            projectionData.push({
                year,
                age,
                balance: Math.round(balance),
                income: Math.round(actualIncome),
                minDrawdown: Math.round(minDrawdown),
                lumpSum: Math.round(lumpSum),
                investmentReturn: Math.round(investmentReturn),
                fees: Math.round(fees),
                agePension: Math.round(agePension),
                totalIncome: Math.round(actualIncome + agePension),
                netCashFlow: Math.round(investmentReturn - actualIncome - fees - lumpSum)
            });
            
            // Update for next year
            balance = balance + investmentReturn - actualIncome - fees - lumpSum;
            if (balance < 0) balance = 0;
            age++;
            if (inputs.hasPartner) partnerAge++;
        }
        
        // Calculate summary stats
        const finalBalance = projectionData[projectionData.length - 1].balance;
        const avgIncome = projectionData.reduce((sum, p) => sum + p.totalIncome, 0) / projectionData.length;
        const totalDrawdown = projectionData.reduce((sum, p) => sum + p.income, 0);
        const totalAgePension = projectionData.reduce((sum, p) => sum + p.agePension, 0);
        const yearsUntilDepletion = projectionData.findIndex(p => p.balance <= 0);
        
        setResults({
            projectionData,
            summary: {
                scenario: scenario.name,
                pensionType: pensionType.name,
                finalBalance,
                avgIncome,
                totalDrawdown,
                totalAgePension,
                yearsUntilDepletion: yearsUntilDepletion === -1 ? inputs.projectionYears : yearsUntilDepletion,
                sustainable: yearsUntilDepletion === -1 || yearsUntilDepletion >= inputs.projectionYears
            }
        });
    };

    useEffect(() => {
        if (inputs.currentBalance > 0) {
            runQuickProjection();
        }
    }, [selectedScenario, selectedPensionType, inputs]);

    return (
        <div className="space-y-6">
            {/* Header with disclaimer */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg border-2 border-blue-200">
                <div className="flex items-start gap-4">
                    <div className="text-4xl">⚡</div>
                    <div className="flex-1">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Quick Retirement Check</h2>
                        <p className="text-gray-700 mb-3">
                            Get a quick 30-second snapshot of your retirement projections across different scenarios.
                        </p>
                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                            <p className="text-sm text-gray-700">
                                <strong>⚠️ Quick Check Only:</strong> This tool uses simplified assumptions for rapid estimates. 
                                For comprehensive planning with historical market data (1928-2025), Monte Carlo simulation (1000 trials), 
                                and 4 withdrawal strategies, use the <strong>Advanced Calculator</strong>.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-gray-200">
                <button
                    onClick={() => setActiveTab('scenarios')}
                    className={`px-4 py-2 font-medium ${activeTab === 'scenarios' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-600 hover:text-gray-800'}`}
                >
                    📊 3-Scenario Check
                </button>
                <button
                    onClick={() => setActiveTab('pension_types')}
                    className={`px-4 py-2 font-medium ${activeTab === 'pension_types' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-600 hover:text-gray-800'}`}
                >
                    💰 Pension Type Comparison
                </button>
            </div>

            {activeTab === 'scenarios' && (
                <div className="space-y-6">
                    {/* Inputs */}
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <h3 className="text-lg font-semibold mb-4">Your Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Current Age</label>
                                <input
                                    type="number"
                                    value={inputs.currentAge}
                                    onChange={(e) => setInputs({...inputs, currentAge: Number(e.target.value)})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Current Balance ($)</label>
                                <input
                                    type="number"
                                    value={inputs.currentBalance}
                                    onChange={(e) => setInputs({...inputs, currentBalance: Number(e.target.value)})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Desired Income ($/yr)</label>
                                <input
                                    type="number"
                                    value={inputs.desiredIncome}
                                    onChange={(e) => setInputs({...inputs, desiredIncome: Number(e.target.value)})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Projection Years</label>
                                <input
                                    type="number"
                                    value={inputs.projectionYears}
                                    onChange={(e) => setInputs({...inputs, projectionYears: Number(e.target.value)})}
                                    min="5"
                                    max="35"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={inputs.homeOwned}
                                    onChange={(e) => setInputs({...inputs, homeOwned: e.target.checked})}
                                    className="w-4 h-4"
                                />
                                <label className="text-sm font-medium text-gray-700">Homeowner</label>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={inputs.includePension}
                                    onChange={(e) => setInputs({...inputs, includePension: e.target.checked})}
                                    className="w-4 h-4"
                                />
                                <label className="text-sm font-medium text-gray-700">Include Age Pension</label>
                            </div>
                        </div>

                        {/* Optional: Lump Sum */}
                        <div className="mt-4 pt-4 border-t">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">Optional: One-Off Lump Sum Withdrawal</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Year of Withdrawal</label>
                                    <input
                                        type="number"
                                        value={inputs.lumpSumYear}
                                        onChange={(e) => setInputs({...inputs, lumpSumYear: Number(e.target.value)})}
                                        min="0"
                                        max={inputs.projectionYears}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Amount ($)</label>
                                    <input
                                        type="number"
                                        value={inputs.lumpSumAmount}
                                        onChange={(e) => setInputs({...inputs, lumpSumAmount: Number(e.target.value)})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Scenario Selector */}
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <h3 className="text-lg font-semibold mb-4">Select Scenario</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {Object.entries(scenarios).map(([key, scenario]) => (
                                <button
                                    key={key}
                                    onClick={() => setSelectedScenario(key)}
                                    className={`p-4 rounded-lg border-2 transition-all ${
                                        selectedScenario === key 
                                            ? 'border-blue-500 bg-blue-50' 
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <div className="text-3xl mb-2">{scenario.icon}</div>
                                    <div className="font-semibold text-gray-900">{scenario.name}</div>
                                    <div className="text-sm text-gray-600 mt-1">{scenario.description}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Results */}
                    {results && (
                        <>
                            {/* Summary Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="bg-white p-4 rounded-lg shadow">
                                    <div className="text-sm text-gray-600">Final Balance</div>
                                    <div className="text-2xl font-bold text-gray-900">
                                        ${results.summary.finalBalance.toLocaleString()}
                                    </div>
                                    <div className={`text-sm ${results.summary.sustainable ? 'text-green-600' : 'text-red-600'}`}>
                                        {results.summary.sustainable ? '✅ Sustainable' : '⚠️ Depleted'}
                                    </div>
                                </div>
                                <div className="bg-white p-4 rounded-lg shadow">
                                    <div className="text-sm text-gray-600">Avg Annual Income</div>
                                    <div className="text-2xl font-bold text-gray-900">
                                        ${Math.round(results.summary.avgIncome).toLocaleString()}
                                    </div>
                                    <div className="text-sm text-gray-600">per year</div>
                                </div>
                                <div className="bg-white p-4 rounded-lg shadow">
                                    <div className="text-sm text-gray-600">Total Age Pension</div>
                                    <div className="text-2xl font-bold text-gray-900">
                                        ${Math.round(results.summary.totalAgePension).toLocaleString()}
                                    </div>
                                    <div className="text-sm text-gray-600">over {inputs.projectionYears} years</div>
                                </div>
                                <div className="bg-white p-4 rounded-lg shadow">
                                    <div className="text-sm text-gray-600">Years Until Depletion</div>
                                    <div className="text-2xl font-bold text-gray-900">
                                        {results.summary.yearsUntilDepletion}
                                    </div>
                                    <div className="text-sm text-gray-600">years</div>
                                </div>
                            </div>

                            {/* Chart */}
                            <div className="bg-white p-6 rounded-lg shadow-md">
                                <h3 className="text-lg font-semibold mb-4">Balance Projection ({results.summary.scenario} Scenario)</h3>
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart data={results.projectionData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="year" label={{ value: 'Year', position: 'insideBottom', offset: -5 }} />
                                        <YAxis label={{ value: 'Balance ($)', angle: -90, position: 'insideLeft' }} />
                                        <Tooltip 
                                            formatter={(value) => `$${value.toLocaleString()}`}
                                            labelFormatter={(label) => `Year ${label}`}
                                        />
                                        <Legend />
                                        <Line type="monotone" dataKey="balance" stroke="#3b82f6" strokeWidth={2} name="Balance" />
                                        <ReferenceLine y={0} stroke="red" strokeDasharray="3 3" />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>

                            {/* CTA to Advanced Calculator */}
                            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg border-2 border-green-200">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                                            🚀 Want a Comprehensive Analysis?
                                        </h3>
                                        <p className="text-gray-700 mb-2">
                                            The Advanced Calculator provides:
                                        </p>
                                        <ul className="text-sm text-gray-600 space-y-1 mb-3">
                                            <li>✅ Historical market data (1928-2025) - see how you'd perform in actual market conditions</li>
                                            <li>✅ Monte Carlo simulation (1000 trials) - understand probability of success</li>
                                            <li>✅ 4 withdrawal strategies (Fixed, Dynamic, Guardrails, Floor/Ceiling)</li>
                                            <li>✅ Phased retirement modeling (partners retire at different ages)</li>
                                            <li>✅ Alternative assets (property, infrastructure, collectibles)</li>
                                            <li>✅ What-if scenarios (sequence risk, fee changes, longer life)</li>
                                        </ul>
                                    </div>
                                    <button
                                        onClick={() => window.location.href = '/advanced-calculator'}
                                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow-lg transition-all whitespace-nowrap"
                                    >
                                        Open Advanced Calculator →
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {activeTab === 'pension_types' && (
                <div className="space-y-6">
                    {/* Pension Type Selector */}
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <h3 className="text-lg font-semibold mb-4">Compare Pension Types</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            {Object.entries(pensionTypes).map(([key, type]) => (
                                <button
                                    key={key}
                                    onClick={() => setSelectedPensionType(key)}
                                    className={`p-4 rounded-lg border-2 transition-all ${
                                        selectedPensionType === key 
                                            ? 'border-blue-500 bg-blue-50' 
                                            : 'border-gray-200 hover:border-gray-300'
                                    } ${type.notAvailableSMSF ? 'opacity-60' : ''}`}
                                >
                                    <div className="text-3xl mb-2">{type.icon}</div>
                                    <div className="font-semibold text-gray-900">{type.name}</div>
                                    <div className="text-xs text-gray-600 mt-1">{type.description}</div>
                                    {type.notAvailableSMSF && (
                                        <div className="text-xs text-red-600 mt-2 font-semibold">❌ Not available in SMSF</div>
                                    )}
                                </button>
                            ))}
                        </div>

                        {/* Selected pension type details */}
                        <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 rounded-lg">
                            <h4 className="text-lg font-bold text-gray-900 mb-3">
                                {pensionTypes[selectedPensionType].icon} {pensionTypes[selectedPensionType].name}
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h5 className="font-semibold text-gray-700 mb-2">Features:</h5>
                                    <ul className="space-y-2 text-sm">
                                        {pensionTypes[selectedPensionType].features.map((feature, idx) => (
                                            <li key={idx} className="text-gray-700">{feature}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <h5 className="font-semibold text-gray-700 mb-2">Characteristics:</h5>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Flexibility:</span>
                                            <span className="font-semibold">{pensionTypes[selectedPensionType].flexibility}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Investment Risk:</span>
                                            <span className="font-semibold">{pensionTypes[selectedPensionType].risk}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Estate Benefit:</span>
                                            <span className="font-semibold">{pensionTypes[selectedPensionType].estate}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Tax Rate (60+):</span>
                                            <span className="font-semibold">{pensionTypes[selectedPensionType].taxRate * 100}%</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Comparison projection with selected type */}
                    {results && (
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h3 className="text-lg font-semibold mb-4">
                                Projection: {pensionTypes[selectedPensionType].name}
                            </h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={results.projectionData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="year" />
                                    <YAxis yAxisId="left" />
                                    <YAxis yAxisId="right" orientation="right" />
                                    <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                                    <Legend />
                                    <Line yAxisId="left" type="monotone" dataKey="balance" stroke="#3b82f6" strokeWidth={2} name="Balance" />
                                    <Line yAxisId="right" type="monotone" dataKey="totalIncome" stroke="#10b981" strokeWidth={2} name="Total Income" />
                                </LineChart>
                            </ResponsiveContainer>

                            {selectedPensionType === 'transition_to_retirement' && (
                                <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                                    <p className="text-sm text-gray-700">
                                        <strong>💡 TTR Tip:</strong> You can salary sacrifice more to super (tax deductible at your marginal rate) 
                                        while drawing pension income (tax-free if 60+). This can significantly boost your retirement savings 
                                        while maintaining cash flow.
                                    </p>
                                </div>
                            )}

                            {selectedPensionType === 'lifetime_annuity' && (
                                <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-400 rounded">
                                    <p className="text-sm text-gray-700">
                                        <strong>⚠️ SMSF Limitation:</strong> Lifetime annuities are not available within SMSF structures. 
                                        To access this option, you would need to roll over funds to a retail super fund or purchase 
                                        an annuity product outside of super.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

SMSFQuickRetirementCheck.propTypes = {
    smsfState: PropTypes.object.isRequired,
    setSmsfState: PropTypes.func.isRequired
};

export default SMSFQuickRetirementCheck;





























































